import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import {
  Signal, RefreshCw, ExternalLink, Info, Activity, Circle, Users, Wifi, WifiOff,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { fmtMDT, fmtMDTShort } from "../lib/time";

function Kpi({ icon: Icon, label, value, sub, tone = "primary", testId }) {
  const toneCls = {
    rose: "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-300",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
    primary: "border-primary/30 bg-primary/5 text-primary",
  }[tone] || "border-border bg-card";
  return (
    <div className={cn("rounded-2xl border p-4 flex items-start gap-3", toneCls)} data-testid={testId}>
      <div className="w-9 h-9 rounded-xl bg-background/60 border border-current/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
        <div className="text-[26px] font-bold tabular-nums leading-none mt-1">{value}</div>
        {sub && <div className="text-[11.5px] opacity-70 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

export default function PingPlotterApp() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const r = await api.get(`/pingplotter/summary${force ? "?force=true" : ""}`);
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const agents = data?.agents || [];
  const trend = data?.latency_24h || [];

  return (
    <div className="space-y-5" data-testid="pingplotter-page">
      <div className="text-[12px] text-muted-foreground inline-flex items-center gap-1 flex-wrap">
        <Link to="/" className="hover:text-foreground">Dashboard</Link>
        <span aria-hidden>›</span>
        <Link to="/apps" className="hover:text-foreground">Apps</Link>
        <span aria-hidden>›</span>
        <span className="text-foreground font-medium">PingPlotter</span>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            <Signal className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-[24px] font-semibold tracking-tight leading-tight">PingPlotter Cloud</h1>
            <p className="text-[13px] text-muted-foreground mt-1">
              Latency &amp; packet-loss monitoring for remote agents — live from PingPlotter Cloud.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data?.updated_at && <span className="text-[11.5px] text-muted-foreground">Updated {fmtMDT(data.updated_at)}</span>}
          <Button variant="outline" size="sm" onClick={() => load(true)} className="h-9 gap-1.5" data-testid="pp-refresh-btn">
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} /> Refresh
          </Button>
          <a href="https://pingplotter.cloud/" target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 h-9 text-[12.5px] hover:border-primary/40" data-testid="pp-open-portal">
            Open in PingPlotter <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {data?.mocked && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-800 dark:text-amber-200 p-3 text-[12.5px] inline-flex items-start gap-2 max-w-4xl">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>Sample data.</strong> Add <code>PINGPLOTTER_API_KEY</code> and <code>PINGPLOTTER_SUBDOMAIN</code>
            to <code>backend/.env</code> to flip this dashboard to live PingPlotter Cloud data.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Users}    label="Agents"         value={data?.agents_total ?? "—"} sub={`${data?.agents_online ?? 0} online`} tone="primary" testId="pp-kpi-agents" />
        <Kpi icon={Activity} label="Open sessions"  value={data?.sessions_open ?? "—"} sub="Live traces" tone="primary" testId="pp-kpi-sessions" />
        <Kpi icon={Signal}   label="Avg latency"    value={data?.avg_latency_ms != null ? `${data.avg_latency_ms} ms` : "—"} sub="Across all live agents" tone={(data?.avg_latency_ms || 0) > 80 ? "rose" : "emerald"} testId="pp-kpi-latency" />
        <Kpi icon={WifiOff}  label="Packet loss"    value={data?.avg_loss_pct != null ? `${data.avg_loss_pct}%` : "—"} sub="24h weighted average" tone={(data?.avg_loss_pct || 0) > 1 ? "rose" : "emerald"} testId="pp-kpi-loss" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[13.5px] font-semibold">Latency — last 24 hours</div>
              <div className="text-[11.5px] text-muted-foreground">Average round-trip time (ms) across all online agents</div>
            </div>
            <Signal className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="h-[260px]">
            <ResponsiveContainer>
              <LineChart data={trend} margin={{ top: 4, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={36} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="avg_ms"   name="Latency (ms)" stroke="#49C22E" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="loss_pct" name="Loss (%)"     stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[13.5px] font-semibold">Agents</div>
              <div className="text-[11.5px] text-muted-foreground">{agents.length} configured</div>
            </div>
            <Users className="w-4 h-4 text-muted-foreground" />
          </div>
          <ul className="space-y-1.5 max-h-[240px] overflow-y-auto tabler-scroll">
            {agents.map((a) => (
              <li key={a.name} data-testid={`pp-agent-${a.name}`} className="flex items-center gap-2 rounded-md border border-border bg-background/60 px-2.5 py-1.5">
                <Circle className={cn("w-2 h-2 shrink-0", a.online ? "text-emerald-500" : "text-muted-foreground/50")} fill="currentColor" strokeWidth={0} />
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-medium truncate">{a.name}</div>
                  <div className="text-[10.5px] text-muted-foreground truncate">
                    {a.platform} · {a.online ? `${a.sessions} session${a.sessions === 1 ? "" : "s"}` : `last seen ${fmtMDTShort(a.last_seen)}`}
                  </div>
                </div>
                {a.avg_latency_ms != null && (
                  <div className="text-right text-[10.5px] tabular-nums shrink-0">
                    <div className={cn("font-semibold", a.avg_latency_ms > 80 ? "text-rose-500" : "text-emerald-500")}>{a.avg_latency_ms}ms</div>
                    <div className={cn(a.packet_loss_pct > 1 ? "text-rose-500" : "text-muted-foreground")}>{a.packet_loss_pct?.toFixed?.(1) ?? 0}% loss</div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
